import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiDollarSign, FiHome, FiUsers, FiFileText, FiAward } from 'react-icons/fi';
import { propertyAPI, clientAPI, mandateAPI, saleAPI } from '../../services/api';
import { toast } from 'react-toastify';

const LAND_TITLE_TYPES = [
  { value: 'titre_foncier', label: 'Titre Foncier (TF)' },
  { value: 'concession_rurale', label: 'Concession Rurale' },
  { value: 'permis_occuper', label: 'Permis d\'Occuper (PO)' },
  { value: 'lettre_attribution', label: 'Lettre d\'Attribution' },
  { value: 'bail_emphyteotique', label: 'Bail Emphytéotique' },
  { value: 'coutumier', label: 'Droit Coutumier' },
  { value: 'autre', label: 'Autre document foncier' },
];

const SALE_STATUSES = [
  { value: 'prospect', label: 'Prospect / Négociation' },
  { value: 'offer_made', label: 'Offre d\'achat déposée' },
  { value: 'offer_accepted', label: 'Offre acceptée' },
  { value: 'compromis_signed', label: 'Compromis de vente signé' },
  { value: 'conditions_cleared', label: 'Conditions suspensives levées' },
  { value: 'deed_signed', label: 'Acte notarié signé' },
  { value: 'completed', label: 'Vente clôturée (Finalisée)' },
  { value: 'cancelled', label: 'Annulée' },
];

const SaleFormModal = ({ isOpen, onClose, onSuccess, saleToEdit }) => {
  const isEditing = Boolean(saleToEdit);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    property_id: '',
    transaction_nature: 'direct_sale',
    seller_client_id: '',
    buyer_client_id: '',
    mandate_id: '',
    land_title_type: 'titre_foncier',
    land_title_number: '',
    land_area: '',
    listed_price: '',
    agreed_sale_price: '',
    deposit_amount: '',
    agency_commission_rate: 5.0,
    commission_payer: 'seller',
    notary_fees_estimate: '',
    notary_name: '',
    notary_phone: '',
    notary_email: '',
    notary_address: '',
    deed_signature_date: '',
    status: 'prospect',
    acquisition_price: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen) {
      loadDependencies();
      if (saleToEdit) {
        setFormData({
          property_id: saleToEdit.property_id || '',
          transaction_nature: saleToEdit.transaction_nature || 'direct_sale',
          seller_client_id: saleToEdit.seller_client_id || '',
          buyer_client_id: saleToEdit.buyer_client_id || '',
          mandate_id: saleToEdit.mandate_id || '',
          land_title_type: saleToEdit.land_title_type || 'titre_foncier',
          land_title_number: saleToEdit.land_title_number || '',
          land_area: saleToEdit.land_area || '',
          listed_price: saleToEdit.listed_price || '',
          agreed_sale_price: saleToEdit.agreed_sale_price || '',
          deposit_amount: saleToEdit.deposit_amount || '',
          agency_commission_rate: saleToEdit.agency_commission_rate || 5.0,
          commission_payer: saleToEdit.commission_payer || 'seller',
          notary_fees_estimate: saleToEdit.notary_fees_estimate || '',
          notary_name: saleToEdit.notary_name || '',
          notary_phone: saleToEdit.notary_phone || '',
          notary_email: saleToEdit.notary_email || '',
          notary_address: saleToEdit.notary_address || '',
          deed_signature_date: saleToEdit.deed_signature_date ? saleToEdit.deed_signature_date.split('T')[0] : '',
          status: saleToEdit.status || 'prospect',
          acquisition_price: saleToEdit.acquisition_price || '',
          notes: saleToEdit.notes || '',
        });
      } else {
        setFormData({
          property_id: '',
          transaction_nature: 'direct_sale',
          seller_client_id: '',
          buyer_client_id: '',
          mandate_id: '',
          land_title_type: 'titre_foncier',
          land_title_number: '',
          land_area: '',
          listed_price: '',
          agreed_sale_price: '',
          deposit_amount: '',
          agency_commission_rate: 5.0,
          commission_payer: 'seller',
          notary_fees_estimate: '',
          notary_name: '',
          notary_phone: '',
          notary_email: '',
          notary_address: '',
          deed_signature_date: '',
          status: 'prospect',
          acquisition_price: '',
          notes: '',
        });
      }
    }
  }, [isOpen, saleToEdit]);

  const loadDependencies = async () => {
    try {
      const [propRes, clientRes, mandateRes] = await Promise.all([
        propertyAPI.list({ limit: 100 }),
        clientAPI.list({ limit: 100 }),
        mandateAPI.list({ limit: 100 }),
      ]);
      setProperties(propRes.data.data || propRes.data || []);
      setClients(clientRes.data.data || clientRes.data || []);
      setMandates(mandateRes.data.data || mandateRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement des données');
    }
  };

  const handlePropertyChange = (propertyId) => {
    const prop = properties.find(p => String(p.id) === String(propertyId));
    setFormData(prev => ({
      ...prev,
      property_id: propertyId,
      listed_price: prev.listed_price || (prop?.price ? String(prop.price) : ''),
      land_area: prev.land_area || (prop?.surface_area ? String(prop.surface_area) : ''),
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculs dynamiques
  const basePrice = parseFloat(formData.agreed_sale_price || formData.listed_price || 0);
  const estimatedCommission = (basePrice * parseFloat(formData.agency_commission_rate || 5)) / 100;
  const estimatedCapitalGain = formData.transaction_nature === 'resale' && formData.acquisition_price 
    ? Math.max(0, basePrice - parseFloat(formData.acquisition_price))
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) return toast.warn('Veuillez sélectionner un bien immobilier.');
    if (!formData.listed_price || parseFloat(formData.listed_price) <= 0) return toast.warn('Le prix de vente est obligatoire.');

    setSaving(true);
    try {
      if (isEditing) {
        await saleAPI.update(saleToEdit.id, formData);
        toast.success('Dossier de transaction mis à jour avec succès !');
      } else {
        await saleAPI.create(formData);
        toast.success('Dossier de vente/achat créé avec succès !');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content card" style={modalContentStyle}>
        {/* ── Header Sticky ── */}
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
              <FiDollarSign color="var(--primary, #2563eb)" size={20} />
              {isEditing ? `Modifier la Transaction #${saleToEdit.sale_number}` : 'Nouvelle Transaction Immobilière (Achat / Vente / Revente)'}
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-secondary, #64748b)' }}>
              Gestion foncière, financière, séquestre et notariée de l'opération.
            </p>
          </div>
          <button onClick={onClose} style={closeBtnStyle} title="Fermer"><FiX size={20} /></button>
        </div>

        {/* ── Formulaire avec Scroll Interne Fluide ── */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* ── SECTION 1: Nature & Bien Immobilier ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiHome size={15} color="#2563eb" /> 1. Nature de l'Opération & Bien Immobilier
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Nature de l'Opération *</label>
                  <select 
                    className="form-control" 
                    value={formData.transaction_nature} 
                    onChange={e => handleChange('transaction_nature', e.target.value)}
                    required
                  >
                    <option value="direct_sale">Vente Directe</option>
                    <option value="resale">Revente (Bien déjà acquis / Calcul plus-value)</option>
                    <option value="purchase_for_client">Achat pour Compte Client / Mandat d'Acquisition</option>
                    <option value="vefa_off_plan">Vente sur Plan (VEFA)</option>
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Bien Immobilier *</label>
                  <select 
                    className="form-control" 
                    value={formData.property_id} 
                    onChange={e => handlePropertyChange(e.target.value)}
                    required
                  >
                    <option value="">— Choisir un bien —</option>
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.title} ({p.city || 'Bamako'}{p.neighborhood ? `, ${p.neighborhood}` : ''})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Mandat Associé (Optionnel)</label>
                  <select 
                    className="form-control" 
                    value={formData.mandate_id} 
                    onChange={e => handleChange('mandate_id', e.target.value)}
                  >
                    <option value="">— Aucun mandat lié —</option>
                    {mandates.map(m => (
                      <option key={m.id} value={m.id}>
                        Mandat {m.mandate_number} ({m.type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ── SECTION 2: Parties Contractantes ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiUsers size={15} color="#7c3aed" /> 2. Parties Contractantes (Vendeur / Acquéreur)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Vendeur (Propriétaire actuel)</label>
                  <select 
                    className="form-control" 
                    value={formData.seller_client_id} 
                    onChange={e => handleChange('seller_client_id', e.target.value)}
                  >
                    <option value="">— Sélectionner le vendeur —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} ({c.phone || c.email || 'Client'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Acquéreur (Acheteur / Investisseur)</label>
                  <select 
                    className="form-control" 
                    value={formData.buyer_client_id} 
                    onChange={e => handleChange('buyer_client_id', e.target.value)}
                  >
                    <option value="">— Sélectionner l'acquéreur —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} ({c.phone || c.email || 'Client'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Statut du Dossier *</label>
                  <select 
                    className="form-control" 
                    value={formData.status} 
                    onChange={e => handleChange('status', e.target.value)}
                    required
                  >
                    {SALE_STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ── SECTION 3: Régime Foncier Malien ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiFileText size={15} color="#059669" /> 3. Régime Foncier & Titre de Propriété (Législation Malienne)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Type de Titre Foncier</label>
                  <select 
                    className="form-control" 
                    value={formData.land_title_type} 
                    onChange={e => handleChange('land_title_type', e.target.value)}
                  >
                    {LAND_TITLE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Numéro du Titre / Référence</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ex: TF N° 12458 / Kati"
                    value={formData.land_title_number} 
                    onChange={e => handleChange('land_title_number', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Superficie Foncière (m²)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    className="form-control" 
                    placeholder="Ex: 500"
                    value={formData.land_area} 
                    onChange={e => handleChange('land_area', e.target.value)} 
                  />
                </div>
              </div>
            </div>

            {/* ── SECTION 4: Données Financières, Séquestre & Honoraires ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiDollarSign size={15} color="#dc2626" /> 4. Données Financières, Séquestre & Honoraires
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Prix Demandé / Affiché (XOF) *</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.listed_price} 
                    onChange={e => handleChange('listed_price', e.target.value)} 
                    required
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Prix Convenu / Négocié (XOF)</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="Si offre acceptée..."
                    value={formData.agreed_sale_price} 
                    onChange={e => handleChange('agreed_sale_price', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>
                    Acompte / Séquestre au compromis (XOF)
                    <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4, fontSize: 11 }}>
                      (Consigné chez le notaire)
                    </span>
                  </label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="Ex: 10% du prix"
                    value={formData.deposit_amount} 
                    onChange={e => handleChange('deposit_amount', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Commission Agence (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    className="form-control" 
                    value={formData.agency_commission_rate} 
                    onChange={e => handleChange('agency_commission_rate', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Commission à la charge de</label>
                  <select 
                    className="form-control" 
                    value={formData.commission_payer} 
                    onChange={e => handleChange('commission_payer', e.target.value)}
                  >
                    <option value="seller">Vendeur</option>
                    <option value="buyer">Acquéreur</option>
                    <option value="split_50_50">Partagée 50/50</option>
                  </select>
                </div>

                {formData.transaction_nature === 'resale' && (
                  <div className="form-group" style={{ margin: 0, background: '#f0fdf4', padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                    <label style={{ ...labelStyle, color: '#166534' }}>Prix d'Acquisition Initial (XOF)</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      placeholder="Prix d'achat initial"
                      value={formData.acquisition_price} 
                      onChange={e => handleChange('acquisition_price', e.target.value)} 
                    />
                    {estimatedCapitalGain > 0 && (
                      <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginTop: 4 }}>
                        Plus-value estimée : +{estimatedCapitalGain.toLocaleString('fr-FR')} XOF
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Récap Commission */}
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1e40af', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>Commission Agence estimée ({formData.agency_commission_rate || 5}%) :</span>
                <strong style={{ fontSize: 15 }}>{estimatedCommission.toLocaleString('fr-FR')} XOF</strong>
              </div>
            </div>

            {/* ── SECTION 5: Notaire & Acte Authentique ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiAward size={15} color="#d97706" /> 5. Notaire Instrumentaire & Acte Authentique
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Étude Notariale</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ex: Étude Me Traoré, Notaire"
                    value={formData.notary_name} 
                    onChange={e => handleChange('notary_name', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Téléphone Notaire</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="+223 20..."
                    value={formData.notary_phone} 
                    onChange={e => handleChange('notary_phone', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Date Prévisionnelle Acte Notarié</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={formData.deed_signature_date} 
                    onChange={e => handleChange('deed_signature_date', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  <label style={labelStyle}>Observations & Clauses Particulières</label>
                  <textarea 
                    rows={2}
                    className="form-control" 
                    placeholder="Conditions suspensives (obtention de prêt, purge préemption), modalités de paiement..."
                    value={formData.notes} 
                    onChange={e => handleChange('notes', e.target.value)} 
                  />
                </div>
              </div>
            </div>

          </div>

          {/* ── Footer Sticky ── */}
          <div style={modalFooterStyle}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn btn-primary btn-sm" 
              disabled={saving} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, padding: '8px 18px' }}
            >
              <FiCheck /> {saving ? 'Enregistrement...' : (isEditing ? 'Mettre à jour la transaction' : 'Créer la Transaction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000, padding: '16px',
};
const modalContentStyle = {
  maxWidth: 880, width: '100%', maxHeight: 'calc(100vh - 32px)',
  borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
  background: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: '1px solid var(--border, #e2e8f0)',
};
const modalHeaderStyle = {
  padding: '14px 20px', borderBottom: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#f8fafc', flexShrink: 0,
};
const modalFooterStyle = {
  padding: '12px 20px', borderTop: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  background: '#f8fafc', flexShrink: 0,
};
const closeBtnStyle = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--text-secondary, #64748b)', padding: 4, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const sectionBoxStyle = {
  padding: '12px 14px', borderRadius: 8,
  border: '1px solid var(--border, #e2e8f0)', background: '#ffffff',
};
const sectionTitleStyle = {
  fontSize: 12, fontWeight: 700, color: '#1e293b',
  marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em',
  display: 'flex', alignItems: 'center', gap: 6,
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };

export default SaleFormModal;
